// DataForm component for entering bus operation data - Modern Design

import { useState, useEffect } from "react";
import BusStopManager from "./BusStopManager";
import SearchableSelect from "../common/SearchableSelect";
import { getTodayDate } from "../../utils/formatters";
import { getVehicles, getDrivers } from "../../services/api";

const DataForm = ({ onSubmit, editingRecord, onCancelEdit }) => {
  const [formData, setFormData] = useState({
    date: getTodayDate(),
    route: "",
    trip: "",
    license: "",
    driver: "",
    tickets: "",
    monthlyPass: "",
    tripCount: "1",
    daypass_income: "",
  });

  const [busStops, setBusStops] = useState([{ name: "", passengers: "" }]);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);

  // Fetch vehicles from API
  useEffect(() => {
    const fetchVehicles = async () => {
      setLoadingVehicles(true);
      try {
        const result = await getVehicles();
        if (result.success && result.data) {
          setVehicles(result.data);
        }
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoadingVehicles(false);
      }
    };

    fetchVehicles();
  }, []);

  // Fetch drivers from API
  useEffect(() => {
    const fetchDrivers = async () => {
      setLoadingDrivers(true);
      try {
        const result = await getDrivers();
        if (result.success && result.data) {
          setDrivers(result.data);
        }
      } catch (error) {
        console.error("Error fetching drivers:", error);
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchDrivers();
  }, []);

  // Load editing record data
  useEffect(() => {
    if (editingRecord) {
      setFormData({
        date: editingRecord.date || getTodayDate(),
        route: editingRecord.route || "",
        trip: editingRecord.trip || "",
        license: editingRecord.license || "",
        driver: editingRecord.driver || "",
        tickets: (editingRecord.tickets || 0).toString(),
        monthlyPass: (editingRecord.monthlyPass || 0).toString(),
        tripCount: (editingRecord.tripCount || 1).toString(),
        daypass_income: (editingRecord.daypass_income || 0).toString(),
      });
      setBusStops(
        editingRecord.busStops && editingRecord.busStops.length > 0
          ? editingRecord.busStops.map((stop) => ({
              name: stop.name || "",
              passengers: (stop.passengers || 0).toString(),
            }))
          : [{ name: "", passengers: "" }],
      );
    }
  }, [editingRecord]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Filter valid bus stops
    const validBusStops = busStops
      .filter((stop) => stop.name.trim() && parseInt(stop.passengers) > 0)
      .map((stop) => ({
        name: stop.name.trim(),
        passengers: parseInt(stop.passengers),
      }));

    const data = {
      ...formData,
      tickets: parseInt(formData.tickets) || 0,
      monthlyPass: parseInt(formData.monthlyPass) || 0,
      tripCount: parseInt(formData.tripCount) || 1,
      daypass_income: parseInt(formData.daypass_income) || 0,
      busStops: validBusStops,
    };

    onSubmit(data);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      date: getTodayDate(),
      route: "",
      trip: "",
      license: "",
      driver: "",
      tickets: "",
      monthlyPass: "",
      tripCount: "1",
      daypass_income: "",
    });
    setBusStops([{ name: "", passengers: "" }]);
  };

  const handleCancel = () => {
    resetForm();
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-8 mb-8 border border-blue-100">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-300">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {editingRecord ? "✏️ แก้ไขข้อมูลรถ" : "📝 เพิ่มข้อมูลการวิ่งรถ"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editingRecord
                  ? "กรุณาแก้ไขข้อมูลที่ต้องการเปลี่ยนแปลง"
                  : "กรอกข้อมูลการเดินรถประจำวันอย่างละเอียด"}
              </p>
            </div>
          </div>
          {editingRecord && (
            <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-lg border border-amber-300">
              <svg
                className="w-5 h-5 text-amber-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium text-amber-700">
                โหมดแก้ไข
              </span>
            </div>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section 1: ข้อมูลพื้นฐาน */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              📅 ข้อมูลพื้นฐาน
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* วันที่ */}
            <div className="group">
              <label
                htmlFor="date"
                className="text-sm font-bold text-gray-700 mb-2 flex items-center"
              >
                <span className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center mr-2">
                  📆
                </span>
                วันที่
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="date"
                  name="date"
                  required
                  max={getTodayDate()}
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-blue-300 focus:border-blue-500 transition-all duration-300 text-gray-700 font-medium hover:border-blue-400 group-hover:shadow-lg"
                />
              </div>
            </div>

            {/* สายการวิ่งรถ */}
            <div>
              <SearchableSelect
                name="route"
                value={formData.route}
                onChange={handleInputChange}
                options={[
                  { value: "สนามบิน - ราไวย์", label: "สนามบิน - ราไวย์" },
                  { value: "บขส.1 - ป่าตอง", label: "บขส.1 - ป่าตอง" },
                  { value: "รอบเมืองเก่า", label: "รอบเมืองเก่า" },
                ]}
                placeholder="เลือกสายรถ..."
                label="สายการวิ่งรถ"
                icon={
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              />
            </div>

            {/* เที่ยวรถ */}
            <div>
              <SearchableSelect
                name="trip"
                value={formData.trip}
                onChange={handleInputChange}
                options={[
                  { value: "B1", label: "B1" },
                  { value: "B2", label: "B2" },
                  { value: "B3", label: "B3" },
                  { value: "B4", label: "B4" },
                  { value: "B5", label: "B5" },
                  { value: "B6", label: "B6" },
                  { value: "B7", label: "B7" },
                  { value: "B8", label: "B8" },
                  { value: "B9", label: "B9" },
                  { value: "B10", label: "B10" },
                  { value: "B11", label: "B11" },
                  { value: "B12", label: "B12" },
                  { value: "B13", label: "B13" },
                  { value: "STB", label: "STB" },
                  { value: "บขส.2", label: "บขส.2" },
                  { value: "T1", label: "T1" },
                  { value: "T2", label: "T2" },
                  { value: "บัสเสริม", label: "บัสเสริม" },
                ]}
                placeholder="เลือกเที่ยวรถ..."
                label="เที่ยวรถ"
                icon={
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                  </svg>
                }
              />
            </div>

            {/* ป้ายทะเบียน */}
            <div>
              <SearchableSelect
                name="license"
                value={formData.license}
                onChange={handleInputChange}
                options={vehicles}
                placeholder={
                  loadingVehicles ? "กำลังโหลด..." : "เลือกป้ายทะเบียน..."
                }
                label="ป้ายทะเบียน"
                disabled={loadingVehicles}
                icon={
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              />
            </div>

            {/* ชื่อคนขับ */}
            <div>
              <SearchableSelect
                name="driver"
                value={formData.driver}
                onChange={handleInputChange}
                options={drivers}
                placeholder={
                  loadingDrivers ? "กำลังโหลด..." : "เลือกชื่อคนขับ..."
                }
                label="ชื่อคนขับ"
                disabled={loadingDrivers}
                icon={
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              />
            </div>

            {/* จำนวนเที่ยวรถที่วิ่ง */}
            <div className="group">
              <label
                htmlFor="tripCount"
                className="text-sm font-bold text-gray-700 mb-2 flex items-center"
              >
                <span className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center mr-2">
                  🔄
                </span>
                จำนวนเที่ยวรถที่วิ่ง
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="tripCount"
                  name="tripCount"
                  required
                  min="1"
                  placeholder="1"
                  value={formData.tripCount}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-orange-300 focus:border-orange-500 transition-all duration-300 text-gray-700 font-medium hover:border-orange-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">
                    เที่ยว
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: ข้อมูลผู้โดยสาร */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              👥 ข้อมูลผู้โดยสาร
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* จำนวนตั๋ว */}
            <div className="group">
              <label
                htmlFor="tickets"
                className="text-sm font-bold text-gray-700 mb-2 flex items-center"
              >
                <span className="w-7 h-7 bg-pink-100 rounded-lg flex items-center justify-center mr-2">
                  🎫
                </span>
                จำนวนตั๋ว
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="tickets"
                  name="tickets"
                  required
                  min="0"
                  placeholder="0"
                  value={formData.tickets}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-pink-300 focus:border-pink-500 transition-all duration-300 text-gray-700 font-medium hover:border-pink-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">ใบ</span>
                </div>
              </div>
            </div>

            {/* จำนวนบัตรรายเดือน */}
            <div className="group">
              <label
                htmlFor="monthlyPass"
                className="text-sm font-bold text-gray-700 mb-2 flex items-center"
              >
                <span className="w-7 h-7 bg-indigo-100 rounded-lg flex items-center justify-center mr-2">
                  💳
                </span>
                บัตรรายเดือน
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="monthlyPass"
                  name="monthlyPass"
                  required
                  min="0"
                  placeholder="0"
                  value={formData.monthlyPass}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-indigo-300 focus:border-indigo-500 transition-all duration-300 text-gray-700 font-medium hover:border-indigo-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">คน</span>
                </div>
              </div>
            </div>

            {/* ขาย Day Pass */}
            <div className="group">
              <label
                htmlFor="daypass_income"
                className="text-sm font-bold text-gray-700 mb-2 flex items-center"
              >
                <span className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center mr-2">
                  🎟️
                </span>
                ขายบัตร Day Pass
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                  <span className="text-gray-500 text-sm font-medium">฿</span>
                </div>
                <input
                  type="number"
                  id="daypass_income"
                  name="daypass_income"
                  required
                  min="0"
                  placeholder="0"
                  value={formData.daypass_income}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3.5 border-2 border-gray-300 rounded-xl focus:ring-4 focus:ring-emerald-300 focus:border-emerald-500 transition-all duration-300 text-gray-700 font-medium hover:border-emerald-400 group-hover:shadow-lg"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                  <span className="text-gray-400 text-sm font-medium">บาท</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: ป้ายรับส่งผู้โดยสาร */}
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-red-400 to-red-600 rounded-lg flex items-center justify-center shadow-md">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800 ml-3">
              📍 ป้ายรับส่งผู้โดยสาร
            </h3>
          </div>
          <BusStopManager busStops={busStops} onChange={setBusStops} />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4">
          {editingRecord && (
            <button
              type="button"
              onClick={handleCancel}
              className="group px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg
                className="w-5 h-5 group-hover:rotate-12 transition-transform"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              ยกเลิก
            </button>
          )}
          <button
            type="submit"
            className="group px-10 py-4 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl transition-all duration-300 flex items-center gap-3 shadow-xl hover:shadow-2xl transform hover:scale-105"
          >
            <svg
              className="w-6 h-6 group-hover:scale-110 transition-transform"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-lg">
              {editingRecord ? "💾 อัปเดตข้อมูล" : "✅ บันทึกข้อมูล"}
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default DataForm;
